import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';

import { useSearchBlogArticles } from '../utils/useSearchBlogArticles';
import BlogLayout from '../layouts/blog';
import GlobalNav from '../components/GlobalNav';
import Button from '../components/Button';
import HeaderLeft from '../components/headers/HeaderLeft';
import {
  BlogPostCard,
  BlogReadingList,
  FeaturedAuthors,
  FeaturedBlogBanner,
  Categories,
  EmptyBlogs,
  BlogContentWrapper,
  SectionHeader,
} from '../components/blog';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { makeAuthorPath } from '../paths';

function BlogAuthor({
  location,
  pageContext,
  data: {
    allGhostPost: { edges: allArticles },
  },
}) {
  useEffect(() => {
    if (!window.Paperform) {
      const script = document.createElement('script');
      script.src = 'https://paperform.co/__embed.min.js';
      document.body.appendChild(script);
    }
  }, []);

  const {
    label,
    page,
    numberOfPages,
    author,
    featuredArticle,
    activeCategory,
    pageArticleSlugs,
    allAuthorArticleSlugs,
  } = pageContext;

  const {
    search,
    handleSearch,
    articles: displayedArticles,
  } = useSearchBlogArticles({
    allArticles: allArticles.filter(({ node }) =>
      allAuthorArticleSlugs.includes(node.slug),
    ),
    pageArticleSlugs,
  });

  const previousPage = page === 1 ? null : page - 1;
  const nextPage = page === numberOfPages ? null : page + 1;

  return (
    <BlogLayout location={location}>
      <GlobalNav />
      <Helmet>
        <title>Paperform Blog</title>
        <html lang="en" />
        <meta
          name="description"
          content="Helpful guides, inspiring stories, and product updates from Paperform."
        />
        {/** remove canonical as it breaks pagination SEO <link rel="canonical" href="https://paperform.co/blog" /> */}
        <link rel="shortcut icon" href="/favicon.ico" type="image/png" />
        <meta property="og:site_name" content="Paperform Blog" />
        <meta
          property="article:publisher"
          content="https://www.facebook.com/paperform.co"
        />
      </Helmet>

      <HeaderLeft
        title={<span>{label || 'All'} Articles</span>}
        description={
          <Button to="/blog/" solid black style={{ marginBottom: '1rem' }}>
            Go to blog
          </Button>
        }
        image="/website/logo-v3/graphics/4-3.svg"
        wrapperClass="!bg-gold lg:pl-12"
      />
      <BlogContentWrapper>
        {featuredArticle && (
          <FeaturedBlogBanner featuredArticle={featuredArticle.node} />
        )}

        <SearchInput className="mt-12" onChange={handleSearch} value={search} />

        <Categories activeCategory={activeCategory} />

        <SectionHeader label={author.name} img={author.profile_image} />

        <div className="flex flex-wrap justify-center gap-8">
          {search && !displayedArticles.length ? (
            <EmptyBlogs search={search} />
          ) : null}

          {displayedArticles.map(({ node }, i) => (
            <BlogPostCard key={i} node={node} authorSlug={author.slug} />
          ))}
        </div>

        {!search && (
          <Pagination
            previousPage={
              previousPage ? makeAuthorPath(author.slug, previousPage) : null
            }
            nextPage={nextPage ? makeAuthorPath(author.slug, nextPage) : null}
          />
        )}

        <BlogReadingList />
        <FeaturedAuthors />
      </BlogContentWrapper>
    </BlogLayout>
  );
}

export default BlogAuthor;

export const query = graphql`
  query BlogAuthorQuery($articles: [String]) {
    allGhostPost(filter: { slug: { in: $articles } }) {
      edges {
        node {
          id
          slug
          title
          meta_title
          meta_description
          feature_image
          excerpt

          plaintext
          html
          authors {
            name
            id
            slug
            profile_image
            bio
            location
          }
          tags {
            name
            slug
            id
          }
          published_at
          updated_at
        }
      }
    }
  }
`;
